import type { Locale } from 'date-fns'
import { setDefaultOptions } from 'date-fns'
import { ca, de, enGB, es } from 'date-fns/locale'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { getCurrencyIcon, getCurrencyIconV2, index } from '@last/core'

import { configApi, devicesApi } from '@/api/client'
import { useCustomerDisplay } from '@/composables/useCustomerDisplay'
import i18n from '@/i18n'
import images from '@/images'
import CashMachine from '@/integrations/cashmachine/cashmachine'
import Dataphone from '@/integrations/dataphone/dataphone'
import Printers from '@/integrations/printer/printer'
import { getSerial } from '@/integrations/SerialPlugin'
import type { CreateDeviceRequest } from '@/openapi'
import { useAuthStore } from '@/store/auth'
import { useTillStore } from '@/store/till'
import sync from '@/sync/service'
import type { Config, Printer } from '@/types/config'
import type { DeviceConfig } from '@/types/device'
import UpdaterEngine from '@/updater'
import { checkVersions } from '@/utils/deprecated-versions'
import { getDeviceInfo } from '@/utils/device'

export const useConfigStore = defineStore(
  'config',
  () => {
    // @ts-expect-error Partial default for offline mode
    const config = ref<Config>({
      employees: {
        selectionType: 'names' as const
      },
      virtualBrands: [],
      cashTills: [],
      locationCountryCode: 'ES',
      currencyCode: 'EUR',
      paymentMethods: [],
      paymentMethodsConfig: [],
      blindTill: false,
      billCopies: 1,
      locationName: '',
      enableKitchenOrders: true,
      printers: [],
      autoprintReceipts: false,
      autoprintKitchenOrders: false,
      autoprintBills: false,
      dataphoneConfigs: [],
      adyenDataphones: [],
      showTakeAwayTabPopup: false,
      onlyMasterPrinting: false,
      lastProducts: {
        pos: true,
        bookings: true,
        delivery: true,
        attendance: false,
        stock: false
      }
    })

    const mute = ref<boolean>(false)

    const fontSize = ref<number>(16)

    const useSecondaryDisplay = ref<boolean>(false)

    const device = ref<DeviceConfig>({
      id: 'web',
      mode: 'master',
      platform: 'web',
      model: 'web',
      osVersion: 'web',
      appVersion: 'web',
      webviewVersion: 'web',
      isDataphone: false,
      preferredCashMachine: null,
      preferredDataphone: null,
      preferredTill: null,
      useSelfPrinter: null
    })
    const poiid = ref<string | null>(null)

    const virtualBrands = computed(() => {
      const brands = config.value.virtualBrands
      const defaultBrandId = config.value.locationBrandId
      if (!defaultBrandId) return brands
      return [...brands].sort((a, b) => {
        if (a.id === defaultBrandId) return -1
        if (b.id === defaultBrandId) return 1
        return 0
      })
    })

    const tills = computed(() => {
      return config.value.cashTills
    })

    const currencyIcon = computed(() => {
      return getCurrencyIcon(config.value?.currencyCode)
    })

    const lucideCurrencyIcon = computed(() => {
      return getCurrencyIconV2(config.value?.currencyCode)
    })

    const printers = computed(() => {
      return index(config.value.printers, 'id') as Record<string, Printer>
    })

    const appVersion = computed(() => {
      return device.value.appVersion
    })

    const shipmentProviders = computed(() => config.value.shipmentProviders)

    const products = computed(() => config.value.lastProducts)

    const currentAdyenDataphone = computed(() => {
      return (
        config.value.adyenDataphones?.find(dataphone => dataphone.poiid === poiid.value) || null
      )
    })

    async function refreshConfig(inputConfig?: Config) {
      const auth = useAuthStore()
      if (!auth.isAuthenticated) return
      if (inputConfig) {
        config.value = inputConfig
      } else {
        try {
          config.value = await configApi.getConfig()
        } catch {
          // offline mode
        }
      }

      UpdaterEngine.checkForUpdates()

      config.value.virtualBrands.forEach(brand => {
        if (brand.imageId) {
          images.preloadLogo(brand.imageId)
        }
      })
      const language = config.value.language || 'es'
      i18n.global.locale.value = language
      const locales: Record<string, Locale> = {
        es: es,
        ca: ca,
        de: de,
        en: enGB
      }
      setDefaultOptions({
        locale: locales[language]
      })
      Printers.loadPrinters(config.value.printers)
      refreshDeviceInfo()
    }

    async function refreshDeviceInfo() {
      const deviceInfo = await getDeviceInfo()
      if (deviceInfo.platform !== 'web') {
        const auth = useAuthStore()
        try {
          const deviceResponse = await devicesApi.updateDevice({
            deviceId: deviceInfo.id,
            createDeviceRequest: deviceInfo as CreateDeviceRequest
          })
          device.value = deviceResponse
          checkVersions(deviceInfo, deviceResponse.isDataphone)
        } catch (error: any) {
          if (['INVALID_DEVICE'].includes(error.code)) {
            await auth.logout()
          }
        }
      }

      if (useSecondaryDisplay.value) {
        const { showSecondaryDisplay } = useCustomerDisplay()
        showSecondaryDisplay()
      }

      const preferredTill = tills.value?.find(till => till.id === device.value.preferredTill)
      const till = useTillStore()
      if (preferredTill) {
        till.setTill(preferredTill)
      } else if (tills.value.length === 1) {
        till.setTill(tills.value[0])
      } else if (tills.value.length > 1) {
        till.updateSelectedTillIfNull(tills.value[0])
      }

      if (deviceInfo.model === 'Saturn1000F2') {
        poiid.value = await getSerial()
      }

      CashMachine.init(config.value, device.value.preferredCashMachine)
      Dataphone.init(config.value, device.value.preferredDataphone)
    }

    watch(
      fontSize,
      value => {
        document.documentElement.style.setProperty('font-size', `${value}px`)
      },
      { immediate: true }
    )

    function updateShipmentProvider(shipmentProvider: {
      provider: string | null
      ownFleetEnabled: boolean
    }) {
      sync.record('updateShipmentProvider', shipmentProvider)
    }

    function updateManualShipment(manualShipment: boolean) {
      sync.record('updateManualShipment', manualShipment)
    }

    async function updateHardwarePreferences(hardwarePreferences: {
      preferredCashMachine?: string
      preferredDataphone?: string
      preferredTill?: string
      useSelfPrinter?: boolean
    }) {
      if ('preferredCashMachine' in hardwarePreferences)
        CashMachine.init(config.value, hardwarePreferences.preferredCashMachine)

      if ('preferredDataphone' in hardwarePreferences) {
        Dataphone.init(config.value, hardwarePreferences.preferredDataphone)
      }

      device.value = {
        ...device.value,
        ...hardwarePreferences
      }

      if (device.value.id) {
        await devicesApi.updateHardwarePreferences({
          deviceId: device.value.id,
          updateHardwarePreferencesRequest: hardwarePreferences
        })
      }
    }

    return {
      printers,
      config,
      mute,
      fontSize,
      useSecondaryDisplay,
      device,
      virtualBrands,
      tills,
      currencyIcon,
      lucideCurrencyIcon,
      appVersion,
      shipmentProviders,
      currentAdyenDataphone,
      products,

      refreshConfig,
      updateShipmentProvider,
      updateManualShipment,
      updateHardwarePreferences
    }
  },
  {
    persist: true
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
}
